import { isAxiosError } from "axios";
import { EventStatus } from "../enums/EventStatus";
import { IAssignTicket } from "../interfaces/IAssignTicket";
import { ICheckInRosterItem } from "../interfaces/ICheckInRosterItem";
import { ITicketTransactions } from "../interfaces/ITicketTransactions";
import { IEvent } from "../interfaces/IEvent";
import { IEventAttendee } from "../interfaces/IEventAttendee";
import { IEventListAdmin } from "../interfaces/IEventList";
import { IRescheduleImpact } from "../interfaces/IEventScheduleChange";
import { ILocationList } from "../interfaces/ILocationList";
import { IQuickEvent } from "../interfaces/IQuickEvent";
import { IPagedFilterResponse } from "../interfaces/IPagedFilterResponse";
import { IPagedFilterRequest } from "../interfaces/IPagedFilterResults";
import IParticipant from "../interfaces/IParticipant";
import { ITaskComment, ITask, ITaskCount } from "../interfaces/ITasks";
import { ITicketValidationResponse } from "../interfaces/ITicketValidationResponse";
import { createFormData } from "../utils/formDataUtils";
import { buildPagedFilterQuery } from "../utils/queryHelpers";
import { CrudService } from "./crudService";

export interface DuplicateEventRequest {
    title: string;
    startDate: string;
    includeDetails: boolean;
    includeSchedule: boolean;
    includeLocations: boolean;
    includeRegistration: boolean;
    includeTickets: boolean;
    includeCampaigns: boolean;
    includeForms: boolean;
}

export interface TicketEmailResendPreview {
    recipientEmail: string;
    holderId: string;
    assignmentVersion: number;
}

export interface TicketEmailResendRequest extends TicketEmailResendPreview {
    requestId: string;
}

export class TicketEmailResendError extends Error {
    constructor(message: string, public readonly status?: number) {
        super(message);
        this.name = 'TicketEmailResendError';
    }
}

export class EventService extends CrudService<IEvent> {

    constructor(apiClient: any) {
        super(apiClient, '/admin/event');
    }

    /** Extracts a human-readable message from an Axios error response. */
    private extractErrorMessage(error: any): string {
        const data = error?.response?.data;
        if (!data) return error?.message || 'An error occurred';
        if (typeof data === 'string') return data;
        // ASP.NET ProblemDetails / ValidationProblemDetails
        if (typeof data === 'object') {
            // Field-level errors carry the most useful message (binding/annotation failures)
            if (data.errors && typeof data.errors === 'object') {
                const firstField = Object.keys(data.errors)[0];
                const messages = firstField ? data.errors[firstField] : undefined;
                if (Array.isArray(messages) && typeof messages[0] === 'string') {
                    return `${firstField}: ${messages[0]}`;
                }
            }
            if (typeof data.title === 'string') return data.title;
            if (typeof data.message === 'string') return data.message;
            if (typeof data.error === 'string') return data.error;
        }
        return 'An error occurred';
    }


    async getAll<T>(filters?: { status?: string }): Promise<T> {
        try {
            const response = await this.apiClient.get(this.endpoint, {
                params: filters,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }


    /** Returns one bounded page of the company's events matching the title, newest first. */
    async searchByTitle(title: string, pageSize = 20): Promise<IPagedFilterResponse<IEventListAdmin>> {
        try {
            const params: Record<string, string | number | boolean> = { page: 1, pageSize, sortBy: 'UpcomingFirst' };
            if (title.trim()) params.Title = title.trim();
            const response = await this.apiClient.get<IPagedFilterResponse<IEventListAdmin>>(`${this.endpoint}/paged`, { params });
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async create(item: IEvent, image?: File | null): Promise<IEvent> {
        try {
            const formData = createFormData(item, image);
            const response = await this.apiClient.post(this.endpoint, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    /** Creates an event via the single-page quick creation endpoint. */
    async createQuick(item: IQuickEvent, image?: File | null): Promise<IEvent> {
        try {
            const formData = createFormData(item, image);
            const response = await this.apiClient.post(`${this.endpoint}/quick`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async duplicate(id: string, request: DuplicateEventRequest): Promise<IEvent> {
        try {
            const response = await this.apiClient.post<IEvent>(`${this.endpoint}/${id}/duplicate`, request, { timeout: 120_000 });
            if (typeof response?.data?.id !== 'string' || !response.data.id.trim()) {
                throw new Error('Invalid draft response');
            }
            return response.data;
        } catch (error) {
            const uncertain = 'Your draft could not be confirmed. Check your event list before trying again.';
            if (!isAxiosError(error)) throw new Error(uncertain);
            switch (error.response?.status) {
                case 400:
                case 422: {
                    const data: unknown = error.response.data;
                    const message = typeof data === 'string' && data.trim() && !/[<>]/.test(data)
                        ? data.trim() : 'Check your event name, start date and selected settings, then try again.';
                    throw new Error(message);
                }
                case 401:
                    throw new Error('Sign in again, then try duplicating your event.');
                case 403:
                    throw new Error('Your account cannot copy these settings. Check your permissions and plan, or deselect restricted settings and try again.');
                case 404:
                    throw new Error('Your source event is no longer available. Refresh your event list and choose another event.');
                case 409:
                    throw new Error('Your event settings changed. Refresh your event list and try again.');
                case 413:
                    throw new Error('Your event image is too large. Deselect description and image and try again.');
                case 429:
                    throw new Error('You have made too many requests. Wait a moment and try again.');
                default:
                    throw new Error(uncertain);
            }
        }
    }

    async update(id: string, item: IEvent, image?: File | null): Promise<IEvent> {
        try {
            const formData = createFormData(item, image);
            const delegatePathsFromObject = (item.eventDays ?? []).flatMap((day, dayIndex) =>
                (day.eventSessions ?? []).flatMap((session, sessionIndex) =>
                    (session.sessionDelegates ?? []).map((delegate, delegateIndex) => ({
                        path: `eventDays[${dayIndex}].eventSessions[${sessionIndex}].sessionDelegates[${delegateIndex}]`,
                        delegateId: delegate.delegateId,
                        roleId: delegate.roleId,
                        description: delegate.description ?? "",
                    }))
                )
            );

            const delegateEntriesFromFormData = Array.from(formData.entries())
                .filter(([key]) => key.includes("sessionDelegates"))
                .map(([key, value]) => ({ key, value: String(value) }));

            console.log("[Event Update] Delegate payload (object)", delegatePathsFromObject);
            console.log("[Event Update] Delegate payload (form-data entries)", delegateEntriesFromFormData);

            const response = await this.apiClient.put(`${this.endpoint}/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    /** Impact preview shown to the organiser before confirming a reschedule. */
    async getRescheduleImpact(id: string, newStartDate: string, newEndDate: string): Promise<IRescheduleImpact> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/reschedule-impact`, {
                params: { newStartDate, newEndDate },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async getImage(id: string): Promise<string> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/image`);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async getParticipants(id: string): Promise<IParticipant[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/participant`);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async addParticipant(id: string, participant: IParticipant): Promise<IParticipant> {
        try {
            const response = await this.apiClient.post(`${this.endpoint}/${id}/participant`, participant);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async deleteParticipant(eventId: string, id: string): Promise<void> {
        try {
            await this.apiClient.delete(`${this.endpoint}/${eventId}/participant/${id}`);
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async getAttendees(id: string, request: IPagedFilterRequest): Promise<IPagedFilterResponse<IEventAttendee>> {
        try {
            const query = buildPagedFilterQuery(request)
            const response = await this.apiClient.get(`${this.endpoint}/${id}/attendees?${query}`);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async getTicketEmailResend(eventId: string, ticketId: string): Promise<TicketEmailResendPreview> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${eventId}/issued-tickets/${ticketId}/email-resend`);
            if (!response?.data?.recipientEmail || !response.data.holderId || !Number.isInteger(response.data.assignmentVersion))
                throw new TicketEmailResendError('We couldn\'t load the ticket details. Please try again.');
            return response.data;
        } catch (error: unknown) {
            throw this.ticketResendError(error);
        }
    }

    async resendTicketEmail(eventId: string, ticketId: string, request: TicketEmailResendRequest): Promise<void> {
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const response = await this.apiClient.post(`${this.endpoint}/${eventId}/issued-tickets/${ticketId}/email-resend`, request);
                if (response?.status !== 202 || typeof response.data?.queueItemId !== 'string' || !response.data.queueItemId)
                    throw new TicketEmailResendError('We couldn\'t confirm the ticket email request. Please try again.');
                return;
            } catch (error: unknown) {
                if (attempt === 0 && isAxiosError(error) && ['ECONNABORTED', 'ETIMEDOUT'].includes(error.code ?? '')) continue;
                throw this.ticketResendError(error);
            }
        }
    }

    private ticketResendError(error: unknown): TicketEmailResendError {
        if (error instanceof TicketEmailResendError) return error;
        if (isAxiosError<{ message?: string }>(error)) {
            const status = error.response?.status;
            if (status === 401) return new TicketEmailResendError('Your session has expired. Sign in and try again.', status);
            if (status === 403) return new TicketEmailResendError('You do not have permission to resend this ticket. Contact your organiser administrator.', status);
            if (error.response?.data && typeof error.response.data.message === 'string')
                return new TicketEmailResendError(error.response.data.message, status);
            return new TicketEmailResendError('We couldn\'t confirm the ticket email request. Check your connection and try again.', status);
        }
        return new TicketEmailResendError('We couldn\'t prepare the ticket email. Please try again shortly.');
    }

    async getCheckInRoster(id: string): Promise<ICheckInRosterItem[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/checkin-roster`);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async undoCheckIn(id: string, allocationId: string): Promise<void> {
        try {
            await this.apiClient.delete(`${this.endpoint}/${id}/checkin/${allocationId}`);
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async undoDeny(id: string, allocationId: string): Promise<void> {
        try {
            await this.apiClient.delete(`${this.endpoint}/${id}/deny/${allocationId}`);
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async getLocations(id: string): Promise<ILocationList[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${id}/locations`);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async getTicketAssignments(id: string, delegateId?: string): Promise<IAssignTicket[]> {
        try {
            const params = delegateId ? `?delegateId=${delegateId}` : "";
            const response = await this.apiClient.get(`${this.endpoint}/${id}/ticket-assignments${params}`);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async assignTicketToParticipant(id: string, delegateId: string, ticketId?: string, issuedTicketId?: string) {
        try {
            const response = await this.apiClient.post(`${this.endpoint}/${id}/participant/${delegateId}/assign-ticket`, {
                ticketId: ticketId,
                issuedTicketId: issuedTicketId,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async unassignTicketToParticipant(id: string, delegateId: string, issuedTicketId?: string) {
        try {
            const response = await this.apiClient.post(`${this.endpoint}/${id}/participant/${delegateId}/unassign-ticket`, {
                issuedTicketId: issuedTicketId,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async assignCoordinators(id: string, coordinatorIds: string[]): Promise<void> {
        try {
            await this.apiClient.patch(`${this.endpoint}/${id}/assignCoordinators`, coordinatorIds);
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async cancelEvent(id: string) {
        try {
            await this.apiClient.patch(`${this.endpoint}/${id}/cancel`);
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async publish(id: string, categoryId: string, subCategoryId: string, notes: string): Promise<{ eventId: string; categoryId: string; subCategoryId: string; notes: string; status: EventStatus }> {
        try {
            const response = await this.apiClient.patch(`${this.endpoint}/${id}/publish`, { eventId: id, categoryId, subCategoryId, notes });
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async validateTicket(id: string, allocationId: string): Promise<ITicketValidationResponse> {
        try {
            const response = await this.apiClient.post(`${this.endpoint}/${id}/ticket-validation`, { allocationId });
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async confirmTicket(id: string, allocationId: string, confirmed: boolean, description?: string, isManualCheckIn?: boolean): Promise<void> {
        try {
            await this.apiClient.post(`${this.endpoint}/${id}/ticket-confirmation`, { allocationId, confirmed, description, isManualCheckIn });
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async getStatusCounts(): Promise<any> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/status-counts`);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async getTasksForMultipleEvents(eventIds: string[]): Promise<ITask[]> {
        try {
            const response = await this.apiClient.get('/admin/tasks', {
                params: { eventIds: eventIds.join(',') },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async getTaskCounts(eventId: string): Promise<ITaskCount[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${eventId}/task-counts`);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async getEventTasks(eventId: string) {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${eventId}/task`);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async AddTask(eventId: string, taskData: ITask): Promise<ITask> {
        try {
            const response = await this.apiClient.post(`${this.endpoint}/${eventId}/task`, taskData);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async UpdateTask(eventId: string, taskId: string, task: ITask): Promise<ITask> {
        try {
            const response = await this.apiClient.put(`${this.endpoint}/${eventId}/task/${taskId}`, task);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async DeleteTask(eventId: string, taskId: string): Promise<void> {
        try {
            await this.apiClient.delete(`${this.endpoint}/${eventId}/task/${taskId}`);
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async GetTaskComments(eventId: string, taskId: string): Promise<ITaskComment[]> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${eventId}/task/${taskId}/comment`);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async AddTaskComment(eventId: string, taskId: string, comment: ITaskComment): Promise<ITaskComment> {
        try {
            const response = await this.apiClient.post(`${this.endpoint}/${eventId}/task/${taskId}/comment`, comment);
            return response.data;
        } catch (error: any) {
            throw new Error(this.extractErrorMessage(error));
        }
    }

    async hasPaidTickets(eventId: string): Promise<boolean> {
        try {
            const response = await this.apiClient.get(`${this.endpoint}/${eventId}/has-paid-tickets`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || 'An error occurred while checking paid tickets');
        }
    }
}
